import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UtensilsCrossed, BedDouble, Anchor, PartyPopper } from "lucide-react";
import { publicApi } from "@/lib/api";
import { SectionHeading, PropertyCard, EventCard, PressCard, CATEGORY_COPY } from "@/components/public/ui";

const HERO = "/assets/properties/hero-aerial.webp";
const FAMILY = "/assets/login-hero.jpg";

const CATS = [
  { key: "dine", label: "Dine", icon: UtensilsCrossed, img: "/assets/properties/river-stone.jpg" },
  { key: "stay", label: "Stay", icon: BedDouble, img: "/assets/properties/smithfield-station.jpg" },
  { key: "dock", label: "Dock", icon: Anchor, img: "/assets/properties/surry-seafood.jpg" },
  { key: "gather", label: "Gather", icon: PartyPopper, img: "/assets/properties/vintage-tavern.jpg" },
];

const TIMELINE = [
  { year: "1986", text: "Smithfield Station opens, establishing the family's roots in waterfront hospitality." },
  { year: "Early 2000s", text: "Randy and Brian Pack return to help lead the family business." },
  { year: "2018", text: "Pack Brothers Hospitality is formally established alongside Surry Seafood Co." },
  { year: "2019+", text: "Expansion toward Fort Monroe begins with the vision for 37 North." },
  { year: "2024", text: "The portfolio grows with Vintage Tavern, Decoys Seafood and River Stone Chophouse." },
];

export default function Home() {
  const [properties, setProperties] = useState([]);
  const [events, setEvents] = useState([]);
  const [press, setPress] = useState([]);

  useEffect(() => {
    publicApi.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    publicApi.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    publicApi.get("/press").then((r) => setPress(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const featured = properties.find((p) => p.featured) || properties[0];

  return (
    <div>
      {/* 1. HERO (cinematic still) */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden" data-testid="home-hero">
        <img src={HERO} alt="Aerial view of a Pack Brothers waterfront marina at sunset" className="pb-kenburns absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1722]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1722]/95 via-[#0a1722]/55 to-[#0a1722]/40" />
        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-5 pt-24 lg:px-8">
          <h1 className="max-w-3xl font-serif text-[46px] leading-[1.05] text-white sm:text-[68px]">Hospitality, the Pack Brothers Way.</h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">
            A collection of distinctive restaurants, waterfront escapes, marinas, and gathering places rooted in Coastal Virginia.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/portfolio" data-testid="hero-explore-cta" className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-[#18344A] transition-transform hover:-translate-y-0.5">
              Explore Our Properties <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/story" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-white transition-colors hover:bg-white/10">
              Our Story
            </Link>
          </div>
        </div>
      </section>

      {/* 2. INTRODUCTION */}
      <section className="mx-auto max-w-[1280px] px-5 py-24 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="relative">
            <img src={FAMILY} alt="The Pack brothers on the water" className="aspect-[4/5] w-full rounded-2xl object-cover object-top shadow-[0_24px_60px_rgba(24,52,74,0.18)]" />
            <div className="absolute -bottom-6 -right-4 hidden rounded-xl bg-[#18344A] px-6 py-4 text-white shadow-xl sm:block">
              <div className="font-serif text-3xl">Est. 1986</div>
              <div className="text-xs uppercase tracking-wide text-white/70">Family-owned</div>
            </div>
          </div>
          <div>
            <SectionHeading eyebrow="Rooted in Coastal Virginia" title="Rooted Here. Welcoming Everyone."
              intro="Pack Brothers Hospitality is a family-owned group creating memorable dining, lodging, marina, and event experiences across Coastal Virginia. What began as a single riverside station has grown into a collection of distinctive destinations — each with its own character, all held to one standard of genuine hospitality." />
            <Link to="/story" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.06em] text-[#18344A]">
              Discover Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 3. EXPERIENCE NAVIGATION */}
      <section className="bg-[#F5F3EF] py-24">
        <div className="mx-auto max-w-[1280px] px-5 lg:px-8">
          <SectionHeading eyebrow="Ways to Experience" title="Find Your Experience" align="center" className="mb-12" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CATS.map((c) => (
              <Link key={c.key} to={`/portfolio?category=${c.key}`} data-testid={`home-category-${c.key}`}
                className="group relative flex aspect-[3/4] flex-col justify-end overflow-hidden rounded-2xl">
                <img src={c.img} alt={c.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2233]/85 via-[#0f2233]/20 to-transparent" />
                <div className="relative p-6 text-white">
                  <c.icon className="mb-3 h-7 w-7 text-[#B7A58E]" />
                  <h3 className="font-serif text-3xl">{c.label}</h3>
                  <p className="mt-2 text-sm text-white/80">{CATEGORY_COPY[c.key]}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                    Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. FEATURED PORTFOLIO */}
      <section className="mx-auto max-w-[1280px] px-5 py-24 lg:px-8">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <SectionHeading eyebrow="The Portfolio" title={<>Distinctive Places.<br />One Standard of Hospitality.</>} />
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.06em] text-[#18344A]">
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {properties.slice(0, 6).map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* 5. FEATURED PROPERTY */}
      {featured && (
        <section className="relative flex min-h-[70vh] items-end">
          <img src={featured.image} alt={featured.name} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f2233]/90 to-[#0f2233]/20" />
          <div className="relative mx-auto w-full max-w-[1280px] px-5 py-16 lg:px-8">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#B7A58E]">Featured Destination</p>
            <h2 className="font-serif text-[40px] leading-tight text-white sm:text-[56px]">{featured.name}</h2>
            <p className="mt-1 text-sm uppercase tracking-wide text-white/70">{featured.city}, {featured.state}</p>
            <p className="mt-4 max-w-xl text-white/85">{featured.description}</p>
            <Link to={`/portfolio/${featured.slug}`} className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-[#18344A]">
              Discover {featured.name.replace(/^The\s+/, "").split(" ").slice(0,2).join(" ")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* 6. STORY TIMELINE */}
      <section className="bg-[#18344A] py-24 text-white">
        <div className="mx-auto max-w-[1280px] px-5 lg:px-8">
          <SectionHeading eyebrow="Our Story" title="A Family Tradition on the Water" light align="center" className="mb-14" />
          <div className="grid gap-8 md:grid-cols-5">
            {TIMELINE.map((t) => (
              <div key={t.year} className="relative border-t border-white/15 pt-5">
                <div className="font-serif text-3xl text-[#B7A58E]">{t.year}</div>
                <p className="mt-2 text-sm leading-relaxed text-white/75">{t.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/story" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-white hover:bg-white/10">
              Read Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. HAPPENINGS */}
      {events.length > 0 && (
        <section className="mx-auto max-w-[1280px] px-5 py-24 lg:px-8">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <SectionHeading eyebrow="What's On" title="Happening Across Pack Brothers" />
            <Link to="/happenings" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.06em] text-[#18344A]">
              All Happenings <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((e) => <EventCard key={e.id} e={e} />)}
          </div>
        </section>
      )}

      {/* 8. PRESS */}
      {press.length > 0 && (
        <section className="bg-[#F5F3EF] py-24">
          <div className="mx-auto max-w-[1280px] px-5 lg:px-8">
            <SectionHeading eyebrow="In the News" title="Worth Talking About" align="center" className="mb-12" />
            <div className="grid gap-6 md:grid-cols-3">{press.map((p) => <PressCard key={p.id} p={p} />)}</div>
          </div>
        </section>
      )}

      {/* 9. FINAL CTA */}
      <section className="relative flex min-h-[60vh] items-center">
        <img src="/assets/properties/decoys.jpg" alt="Waterfront dining" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0f2233]/75" />
        <div className="relative mx-auto w-full max-w-[1280px] px-5 text-center lg:px-8">
          <h2 className="mx-auto max-w-2xl font-serif text-[40px] leading-tight text-white sm:text-[56px]">Where Will You Go Next?</h2>
          <p className="mx-auto mt-4 max-w-lg text-white/80">From dinner by the water to a weekend escape, discover your next Pack Brothers experience.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/portfolio" className="rounded-full bg-white px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-[#18344A]">Explore Our Portfolio</Link>
            <Link to="/contact" className="rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.06em] text-white hover:bg-white/10">Plan an Event</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
